/*
 * Copyright (c) 2024 Amazon.com, Inc. All Rights Reserved.
 */
import "./ModelTable.css";
import { ModelEntry } from "../../model/ModelEntry";
import { formatUTCDate } from "../../config/DateUtils";
import Select from "react-select";
import { Region } from "../../model/Region";
import { DetectionSystem } from "../../model/DetectionSystem";
import NumberFilter from "../filters/NumberFilter";
import DateFilter from "../filters/DateFilter";
import React, { useState } from "react";
import { DateFilterValues, NumberFilterValues } from "../../model/FilterValues";
import { ModelButtons } from "./ModelButtons";

/**
 * @author stcatai Mar 22, 2024
 */
interface ModelTableProps {
  models: ModelEntry[];
  setRegionFetch: any;
  setDetectionSystemFetch: any;
  setPublishableFetch: any;
  setLabelFetch: any;
  setModelNameFetch: any;
  setLastCommitDescriptionFetch: any;
  setLastCommitAuthorFetch: any;
  setTtlFetch: any;
  setCommitDateFetch: any;
}

export function ModelTable(props: ModelTableProps) {

  const REGION_OPTIONS = ["All", ...Object.values(Region)].map(region => ({value: region, label: region}));
  const DETECTION_SYSTEM_OPTIONS = ["All", ...Object.values(DetectionSystem)].map(system => ({value: system, label: system}));
  const PUBLISHABLE_OPTIONS = ["All", "true", "false"].map(option => ({value: option, label: option}));

  const [regionFilter, setRegionFilter] = useState<string>("");
  const [detectionSystemFilter, setDetectionSystemFilter] = useState<string>("");
  const [modelNameFilter, setModelNameFilter] = useState<string>("");
  const [publishableFilter, setPublishableFilter] = useState<string>("");
  const [ttlFilter, setTtlFilter] = useState<NumberFilterValues>({});
  const [labelsFilter, setLabelsFilter] = useState<string>("");
  const [lastCommitDescriptionFilter, setLastCommitDescriptionFilter] = useState<string>("");
  const [lastCommitAuthorFilter, setLastCommitAuthorFilter] = useState<string>("");
  const [commitDateFilter, setCommitDateFilter] = useState<DateFilterValues>({});

  const setSelectValues = (updatedValue: any, setFilter: any, setFetch: any) => {
    let value = updatedValue?.value || "";
    if (value === "All")
      value = "";
    setFilter(value);
    setFetch(value);
  }

  const setOnKeyDown = (event: any, setFetch: any, value: any) => {
    if (event.key === "Enter")
      setFetch(value);
  }

  return <table className="table table-striped table-bordered">
    <thead>
      <tr>
        <th rowSpan={2}></th>
        <th>Region</th>
        <th>Detection System</th>
        <th>Model Name</th>
        <th>Publishable</th>
        <th>TTL (mins)</th>
        <th>Labels</th>
        <th>Last Commit Description</th>
        <th>Last Commit Author</th>
        <th>Last Commit Date (UTC)</th>
      </tr>
      <tr className="filters">
        <td><Select options={REGION_OPTIONS} value={{value: regionFilter, label: regionFilter}} onChange={newValue => setSelectValues(newValue, setRegionFilter, props.setRegionFetch)}/></td>
        <td><Select options={DETECTION_SYSTEM_OPTIONS} value={{value: detectionSystemFilter, label: detectionSystemFilter}} onChange={newValue => setSelectValues(newValue, setDetectionSystemFilter, props.setDetectionSystemFetch)}/></td>
        <td><input type="text" value={modelNameFilter} onChange={e => setModelNameFilter(e.target.value)} onKeyDown={(e) => setOnKeyDown(e, props.setModelNameFetch, modelNameFilter)}/></td>
        <td><Select options={PUBLISHABLE_OPTIONS} value={{value: publishableFilter, label: publishableFilter}} onChange={newValue => setSelectValues(newValue, setPublishableFilter, props.setPublishableFetch)}/></td>
        <td><NumberFilter values={ttlFilter} setValues={setTtlFilter} setFetch={props.setTtlFetch}/></td>
        <td><input type="text" value={labelsFilter} onChange={e => setLabelsFilter(e.target.value)} onKeyDown={(e) => setOnKeyDown(e, props.setLabelFetch, labelsFilter)}/></td>
        <td><input type="text" value={lastCommitDescriptionFilter} onChange={e => setLastCommitDescriptionFilter(e.target.value)} onKeyDown={(e) => setOnKeyDown(e, props.setLastCommitDescriptionFetch, lastCommitDescriptionFilter)}/></td>
        <td><input type="text" value={lastCommitAuthorFilter} onChange={e => setLastCommitAuthorFilter(e.target.value)} onKeyDown={(e) => setOnKeyDown(e, props.setLastCommitAuthorFetch, lastCommitAuthorFilter)}/></td>
        <td><DateFilter values={commitDateFilter} setValues={setCommitDateFilter} setFetch={props.setCommitDateFetch}/></td>
      </tr>
    </thead>
    <tbody>
      {
        props.models.map((model, idx) => <tr key={idx}>
            <ModelButtons model={model}/>
            <td><span onClick={() => {setRegionFilter(model.region); props.setRegionFetch(model.region);}} className="clickable">{model.region}</span></td>
            <td><span onClick={() => {setDetectionSystemFilter(model.detectionSystem); props.setDetectionSystemFetch(model.detectionSystem);}} className="clickable">{model.detectionSystem}</span></td>
            <td><span onClick={() => {setModelNameFilter(model.modelName); props.setModelNameFetch(model.modelName);}} className="clickable">{model.modelName}</span></td>
            <td><span onClick={() => {setPublishableFilter(model.publishable.valueOf().toString()); props.setPublishableFetch(model.publishable.valueOf().toString());}} className="clickable">{model.publishable.valueOf().toString()}</span></td>
            <td>{model.ttl}</td>
            <td>{model.labels && Array.from(model.labels).map((label, labelIdx) =>
              <span key={labelIdx} onClick={() => {setLabelsFilter(label); props.setLabelFetch(label);}} className="clickable">{label}</span>)}
            </td>
            <td>{model.lastCommitDescription}</td>
            <td><span onClick={() => {setLastCommitAuthorFilter(model.lastCommitAuthor); props.setLastCommitAuthorFetch(model.lastCommitAuthor);}} className="clickable">{model.lastCommitAuthor}</span></td>
            <td>{formatUTCDate(model.lastCommitTimestamp)}</td>
          </tr>)
      }
    </tbody>
  </table>
}